import classNames from 'classnames/bind';
import { decode } from 'he';
import React, { useState } from 'react';
import { Button, ButtonMode, Dropdown, Loader, Modal, ModalWidth } from '@creditsights/styleguide-web';
import { useForm } from 'react-hook-form';
import useFetchUserProfile from '../../../../hooks/profile/useFetchUserProfile';
import { AskAnalystModalSubject } from '../AnalystsWidget';
import useCreateAskAnalyst from '../../../../hooks/analyst/useCreateAskAnalyst';
import { GENERIC_ERROR_TEXT } from '../../../../Base.constants';
import { trackAskAnalyst } from '../../../../utils/trackUsageUtils';
import styles from './AskAnalystModal.module.scss';
import useFetchUserPermissions from '../../../../hooks/permissions/useFetchUserPermissions';
import { useFetchIssuer } from '../../../../hooks/issuers';
import { IssuerDto } from '../../../../services/dtos/issuer/issuer.dto';

const cx = classNames.bind(styles);

export interface AskAnalystModalProps {
  onSuccess: () => void;
  setError: (value: string) => void;
  error: string;
  closeModal: () => void;
  subject: AskAnalystModalSubject;
  companyCoverage?: string[];
}

type RequiredId = { articleId: number } | { issuerId: string } | { analystId: string };

type AskAnalystFormValues = {
  questionText: string;
};

const getRequiredId = (subjectObject: AskAnalystModalSubject): RequiredId => {
  let id;
  if (subjectObject?.analystId) {
    id = { analystId: subjectObject.analystId };
  }
  if (subjectObject?.articleId) {
    id = { articleId: subjectObject.articleId };
  }
  if (subjectObject?.companyId) {
    id = { issuerId: subjectObject.companyId };
  }
  return id;
};

const getCompanyCoverage = (company: IssuerDto) => {
  const coverage: string[] = [];
  if (company.crCoverage) {
    coverage.push('CR');
  }
  if (company.csCoverage) {
    coverage.push('CS');
  }
  if (company.lfiCoverage) {
    coverage.push('LFI');
  }
  return coverage;
};

function getDropdownOptions(userPermissions, companyClassification) {
  const options = [
    {
      text: 'General',
      value: 1,
      key: 1,
    },
  ];

  if (userPermissions.includes('CS') && companyClassification.includes('CS')) {
    options.push({
      text: 'Fundamental Research',
      value: 2,
      key: 2,
    });
  }

  if (userPermissions.includes('CR') && companyClassification.includes('CR')) {
    options.push({
      text: 'Covenant Research',
      value: 3,
      key: 3,
    });
  }

  if (userPermissions.includes('LFI') && companyClassification.includes('LFI')) {
    options.push({
      text: 'News',
      value: 4,
      key: 4,
    });
  }

  return options;
}

const AskAnalystModal = ({
  onSuccess,
  setError,
  error,
  closeModal,
  subject,
}: AskAnalystModalProps): React.ReactElement => {
  const [dropdownValue, setDropdownValue] = useState<number>(1);
  const { isLoading: isLoadingUserProfile } = useFetchUserProfile();
  const { mutate: createAskAnAnalyst, isLoading: isCreatingAskAnalyst } = useCreateAskAnalyst();
  const isModalCloseable = !isCreatingAskAnalyst || !isLoadingUserProfile;
  const { data: userPermissions } = useFetchUserPermissions();
  const userSubscriptions = userPermissions?.userSubscription.split('_') ?? [];

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid, errors },
  } = useForm<AskAnalystFormValues>({
    mode: 'onChange',
    defaultValues: {
      questionText: '',
    },
  });
  const isQuestionTextInputEmpty = watch('questionText').trim().length === 0;
  const companyId = decode(subject?.companyId ?? '');
  const { data: company, isLoading: isLoadingCompany } = useFetchIssuer(companyId, {
    enabled: companyId.includes('company'),
  });

  const renderFooter = () => (
    <>
      <Button
        dataTestId="add-issuers-cancel-button"
        mode={ButtonMode.SECONDARY}
        onClick={closeModal}
        disabled={isCreatingAskAnalyst}
      >
        Cancel
      </Button>
      <Button
        type="submit"
        onClick={handleSubmit(onSubmit)}
        dataTestId="ask-an-analyst-button-modal"
        mode={ButtonMode.PRIMARY}
        disabled={!isValid || isQuestionTextInputEmpty}
        loading={isCreatingAskAnalyst}
      >
        Send Message
      </Button>
    </>
  );

  const subjectName = decode(subject?.subjectName);

  const onSubmit = (data) => {
    setError('');
    createAskAnAnalyst(
      {
        ...data,
        ...getRequiredId(subject),
        questionType: userSubscriptions?.length < 2 ? 1 : dropdownValue,
      },
      {
        onSuccess: () => {
          trackAskAnalyst(subjectName);
          onSuccess();
          closeModal();
        },
        onError: () => {
          setError(GENERIC_ERROR_TEXT);
        },
      }
    );
  };

  return (
    <Modal
      className={classNames(cx('body'), 'to-translate')}
      width={ModalWidth.Medium}
      open
      onClose={closeModal}
      headerTitle="Ask an Analyst"
      footer={renderFooter()}
      closeOnEscape={false}
      closeOnDimmerClick={false}
      dataTestId="ask-an-analyst-modal"
      closeOnButtonClick={isModalCloseable}
      error={error}
    >
      {isLoadingUserProfile ? (
        <div className={cx('loader-container')}>
          <Loader position="center" />
        </div>
      ) : (
        <form className={cx('form')} onSubmit={handleSubmit(onSubmit)}>
          {userSubscriptions.length > 1 && company && !isLoadingCompany && (
            <div className={cx('section-wrapper')}>
              <span className={cx('label')}>Question about</span>
              <Dropdown
                value={dropdownValue}
                className={cx('dropdown')}
                onOptionSelect={(value) => {
                  setDropdownValue(value ? +value : 1);
                }}
                options={getDropdownOptions(userSubscriptions, getCompanyCoverage(company))}
              />
            </div>
          )}
          <div className={cx('section-wrapper')}>
            <span className={cx('label')}>Subject</span>
            <span className={cx('item')} data-testid="ask-an-analyst-modal-subject">
              {subjectName}
            </span>
          </div>
          <label className={cx('section-wrapper')} htmlFor="user-msg">
            <span className={cx('label', 'textarea-label')}>Message:</span>
            <textarea
              placeholder="Please enter your comments or questions here"
              id="user-msg"
              className={cx('textarea', {
                'is-error': !isValid,
              })}
              data-testid="ask-an-analyst-modal-user-msg"
              {...register('questionText', {
                maxLength: { value: 3000, message: 'Message is too long' },
              })}
              autoFocus
            />
          </label>
          {!isValid && (
            <div data-testid="ask-an-analyst-modal-error-field" className={cx('error-field')}>
              <span>{errors.questionText?.message}</span>
            </div>
          )}
        </form>
      )}
    </Modal>
  );
};

export default AskAnalystModal;
